import { ApiError, type ApiErrorKind } from '@nucleus-commerce/api-client/transport';
import { useTranslation } from '@nucleus-commerce/i18n/react';

/** Fallback i18n keys per error kind, under the `errors` namespace. */
export const ERROR_KIND_KEYS: Record<ApiErrorKind, string> = {
  validation: 'kind.validation',
  unauthorized: 'kind.unauthorized',
  forbidden: 'kind.forbidden',
  not_found: 'kind.not_found',
  conflict: 'kind.conflict',
  rate_limited: 'kind.rate_limited',
  server: 'kind.server',
  network: 'kind.network',
  timeout: 'kind.timeout',
  cancelled: 'kind.cancelled',
  unknown: 'kind.unknown',
};

/**
 * Resolution order for a user-facing message:
 *   errors:code.<CODE>  (specific, e.g. errors:code.SKU_TAKEN)
 *   errors:kind.<kind>  (generic per kind)
 * The backend `message` is never shown; it is a developer string.
 */
export function errorMessageKey(error: unknown): {
  keys: string[];
  requestId?: string | undefined;
} {
  if (error instanceof ApiError) {
    return {
      keys: [`code.${error.code}`, ERROR_KIND_KEYS[error.kind]],
      requestId: error.requestId,
    };
  }
  return { keys: [ERROR_KIND_KEYS.unknown] };
}

export function useErrorMessage() {
  const { t } = useTranslation('errors');
  return (error: unknown): string => {
    const { keys } = errorMessageKey(error);
    return t(keys as [string, ...string[]]);
  };
}
